import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import NotificationBadge from './NotificationBadge';
import NotificationToggle from './NotificationToggle';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleLogout = async () => {
    const result = await logout();
    if (result.success) {
      toast.success('Logged out successfully');
      navigate('/');
    }
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <nav className="backdrop-blur-md sticky top-0 z-50 border-b shadow-sm" style={{ background: 'rgba(223, 208, 184, 0.95)', borderColor: 'rgba(148, 137, 121, 0.3)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-2">
          {/* Logo */}
          <div className="flex items-center flex-shrink-0">
            <Link to={user ? "/home" : "/"} className="flex items-center">
              <img 
                src="/images/VESTRIOZ.png" 
                alt="VestrioZ Logo" 
                className="h-14 sm:h-16 w-auto object-contain"
                onError={(e) => {
                  // Fallback to icon if logo image fails to load
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="h-14 sm:h-16 w-14 sm:w-16 rounded-xl flex items-center justify-center shadow-lg" style={{ backgroundColor: '#222831', display: 'none' }}>
                <i className="fas fa-exchange-alt text-white text-xl sm:text-2xl"></i>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center justify-center flex-1 mx-8">
            <div className="flex items-center space-x-2">
              {/* Show all navigation for authenticated users */}
              {user ? (
                <>
                  <Link
                    to="/home"
                    className={`px-3 py-2 rounded-lg font-medium transition-all duration-300 flex items-center whitespace-nowrap ${isActive('/home')
                        ? 'text-white shadow-lg'
                        : 'hover:bg-gray-100'
                      }`}
                    style={{
                      backgroundColor: isActive('/home') ? '#222831' : 'transparent',
                      color: isActive('/home') ? 'white' : '#222831'
                    }}
                  >
                    <i className="fas fa-home mr-2 text-sm"></i>Home
                  </Link>
                  <Link
                    to="/rent-items"
                    className={`px-3 py-2 rounded-lg font-medium transition-all duration-300 flex items-center whitespace-nowrap ${isActive('/rent-items')
                        ? 'text-white shadow-lg'
                        : 'hover:bg-gray-100'
                      }`}
                    style={{
                      backgroundColor: isActive('/rent-items') ? '#222831' : 'transparent',
                      color: isActive('/rent-items') ? 'white' : '#222831'
                    }}
                  >
                    <i className="fas fa-search mr-2 text-sm"></i>Browse
                  </Link>
                  <Link
                    to="/list-items"
                    className={`px-3 py-2 rounded-lg font-medium transition-all duration-300 flex items-center whitespace-nowrap ${isActive('/list-items')
                        ? 'text-white shadow-lg'
                        : 'hover:bg-gray-100'
                      }`}
                    style={{
                      backgroundColor: isActive('/list-items') ? '#222831' : 'transparent',
                      color: isActive('/list-items') ? 'white' : '#222831'
                    }}
                  >
                    <i className="fas fa-plus mr-2 text-sm"></i>List Items
                  </Link>
                  <Link
                    to="/dashboard"
                    className={`px-3 py-2 rounded-lg font-medium transition-all duration-300 flex items-center whitespace-nowrap ${isActive('/dashboard')
                        ? 'text-white shadow-lg'
                        : 'hover:bg-gray-100'
                      }`}
                    style={{
                      backgroundColor: isActive('/dashboard') ? '#222831' : 'transparent',
                      color: isActive('/dashboard') ? 'white' : '#222831'
                    }}
                  >
                    <i className="fas fa-chart-line mr-2 text-sm"></i>Dashboard
                  </Link>
                  <Link
                    to="/membership"
                    className={`px-3 py-2 rounded-lg font-medium transition-all duration-300 flex items-center whitespace-nowrap ${isActive('/membership')
                        ? 'text-white shadow-lg'
                        : 'hover:bg-gray-100'
                      }`}
                    style={{
                      backgroundColor: isActive('/membership') ? '#948979' : 'transparent',
                      color: isActive('/membership') ? 'white' : '#948979'
                    }}
                  >
                    <i className="fas fa-crown mr-2 text-sm"></i>Premium
                  </Link>
                  <Link
                    to="/chat"
                    className={`px-3 py-2 rounded-lg font-medium transition-all duration-300 flex items-center whitespace-nowrap relative ${isActive('/chat')
                        ? 'text-white shadow-lg'
                        : 'hover:bg-gray-100'
                      }`}
                    style={{
                      backgroundColor: isActive('/chat') ? '#393E46' : 'transparent',
                      color: isActive('/chat') ? 'white' : '#393E46'
                    }}
                  >
                    <i className="fas fa-comments mr-2 text-sm"></i>Chat
                    <NotificationBadge />
                  </Link>

                  {/* Admin Link */}
                  {user.isAdmin && (
                    <Link
                      to="/admin"
                      className={`px-3 py-2 rounded-lg font-medium transition-all duration-300 flex items-center whitespace-nowrap ${isActive('/admin')
                          ? 'text-white shadow-lg'
                          : 'hover:bg-gray-100'
                        }`}
                      style={{
                        backgroundColor: isActive('/admin') ? '#393E46' : 'transparent',
                        color: isActive('/admin') ? 'white' : '#393E46'
                      }}
                    >
                      <i className="fas fa-shield-alt mr-2 text-sm"></i>Admin
                    </Link>
                  )}
                </>
              ) : null}
            </div>
          </div>

          {/* User Info & Logout - Only show for authenticated users */}
          {user ? (
            <div className="hidden lg:flex items-center space-x-3 flex-shrink-0">
              <NotificationToggle showLabel={false} className="text-xs px-2 py-1" />
              <span className="text-sm font-medium" style={{ color: '#222831' }}>
                Hello, {user.name}
              </span>
              <button
                onClick={handleLogout}
                className="px-3 py-2 rounded-lg transition-all duration-300 border font-medium text-sm whitespace-nowrap"
                style={{
                  background: 'rgba(239, 68, 68, 0.1)',
                  color: '#dc2626',
                  borderColor: 'rgba(239, 68, 68, 0.3)'
                }}
              >
                <i className="fas fa-sign-out-alt mr-1"></i>Logout
              </button>
            </div>
          ) : (
            <div className="hidden lg:flex items-center space-x-3 flex-shrink-0">
              <Link
                to="/login"
                className="px-3 py-2 rounded-lg transition-all duration-300 border font-medium text-sm whitespace-nowrap"
                style={{
                  color: '#948979',
                  borderColor: 'rgba(148, 137, 121, 0.3)'
                }}
              >
                <i className="fas fa-sign-in-alt mr-1"></i>Login
              </Link>
              <Link
                to="/signup"
                className="px-3 py-2 rounded-lg transition-all duration-300 font-medium text-sm whitespace-nowrap"
                style={{
                  background: '#222831',
                  color: 'white'
                }}
              >
                <i className="fas fa-user-plus mr-1"></i>Sign Up
              </Link>
            </div>
          )}

          {/* Medium Screen Navigation (Tablets) */}
          <div className="hidden md:flex lg:hidden items-center space-x-2 flex-1 justify-center mx-4">
            {/* Show all navigation for authenticated users */}
            {user ? (
              <>
                <Link
                  to="/home"
                  className={`p-2 rounded-lg transition-all duration-300 ${isActive('/home') ? 'text-white shadow-lg' : 'hover:bg-gray-100'
                    }`}
                  style={{
                    backgroundColor: isActive('/home') ? '#222831' : 'transparent',
                    color: isActive('/home') ? 'white' : '#222831'
                  }}
                  title="Home"
                >
                  <i className="fas fa-home text-lg"></i>
                </Link>
                <Link
                  to="/rent-items"
                  className={`p-2 rounded-lg transition-all duration-300 ${isActive('/rent-items') ? 'text-white shadow-lg' : 'hover:bg-gray-100'
                    }`}
                  style={{
                    backgroundColor: isActive('/rent-items') ? '#222831' : 'transparent',
                    color: isActive('/rent-items') ? 'white' : '#222831'
                  }}
                  title="Browse Items"
                >
                  <i className="fas fa-search text-lg"></i>
                </Link>
                <Link
                  to="/list-items"
                  className={`p-2 rounded-lg transition-all duration-300 ${isActive('/list-items') ? 'text-white shadow-lg' : 'hover:bg-gray-100'
                    }`}
                  style={{
                    backgroundColor: isActive('/list-items') ? '#222831' : 'transparent',
                    color: isActive('/list-items') ? 'white' : '#222831'
                  }}
                  title="List Items"
                >
                  <i className="fas fa-plus text-lg"></i>
                </Link>
                <Link
                  to="/dashboard"
                  className={`p-2 rounded-lg transition-all duration-300 ${isActive('/dashboard') ? 'text-white shadow-lg' : 'hover:bg-gray-100'
                    }`}
                  style={{
                    backgroundColor: isActive('/dashboard') ? '#222831' : 'transparent',
                    color: isActive('/dashboard') ? 'white' : '#222831'
                  }}
                  title="Dashboard"
                >
                  <i className="fas fa-chart-line text-lg"></i>
                </Link>
                <Link
                  to="/membership"
                  className={`p-2 rounded-lg transition-all duration-300 ${isActive('/membership') ? 'text-white shadow-lg' : 'hover:bg-gray-100'
                    }`}
                  style={{
                    backgroundColor: isActive('/membership') ? '#948979' : 'transparent',
                    color: isActive('/membership') ? 'white' : '#948979'
                  }}
                  title="Premium"
                >
                  <i className="fas fa-crown text-lg"></i>
                </Link>
                <Link
                  to="/chat"
                  className={`p-2 rounded-lg transition-all duration-300 relative ${isActive('/chat') ? 'text-white shadow-lg' : 'hover:bg-gray-100'
                    }`}
                  style={{
                    backgroundColor: isActive('/chat') ? '#393E46' : 'transparent',
                    color: isActive('/chat') ? 'white' : '#393E46'
                  }}
                  title="Chat"
                >
                  <i className="fas fa-comments text-lg"></i>
                  <NotificationBadge />
                </Link>

                {user.isAdmin && (
                  <Link
                    to="/admin"
                    className={`p-2 rounded-lg transition-all duration-300 ${isActive('/admin') ? 'text-white shadow-lg' : 'hover:bg-gray-100'
                      }`}
                    style={{
                      backgroundColor: isActive('/admin') ? '#393E46' : 'transparent',
                      color: isActive('/admin') ? 'white' : '#393E46'
                    }}
                    title="Admin"
                  >
                    <i className="fas fa-shield-alt text-lg"></i>
                  </Link>
                )}
              </>
            ) : null}
          </div>

          {/* Medium Screen User Info - Only show for authenticated users */}
          {user ? (
            <div className="hidden md:flex lg:hidden items-center space-x-2 flex-shrink-0">
              <span className="text-xs font-medium" style={{ color: '#222831' }}>
                Hello, {user.name}
              </span>
              <button
                onClick={handleLogout}
                className="p-2 rounded-lg transition-all duration-300 border"
                style={{
                  background: 'rgba(239, 68, 68, 0.1)',
                  color: '#dc2626',
                  borderColor: 'rgba(239, 68, 68, 0.3)'
                }}
                title="Logout"
              >
                <i className="fas fa-sign-out-alt"></i>
              </button>
            </div>
          ) : (
            <div className="hidden md:flex lg:hidden items-center space-x-2 flex-shrink-0">
              <Link
                to="/login"
                className="p-2 rounded-lg transition-all duration-300 border"
                style={{
                  color: '#948979',
                  borderColor: 'rgba(148, 137, 121, 0.3)'
                }}
                title="Login"
              >
                <i className="fas fa-sign-in-alt"></i>
              </Link>
              <Link
                to="/signup"
                className="p-2 rounded-lg transition-all duration-300"
                style={{
                  background: '#222831',
                  color: 'white'
                }}
                title="Sign Up"
              >
                <i className="fas fa-user-plus"></i>
              </Link>
            </div>
          )}

          {/* Mobile menu button */}
          <div className="md:hidden flex-shrink-0">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 focus:outline-none transition-colors"
              style={{ color: '#222831' }}
            >
              <i className={`fas ${isMenuOpen ? 'fa-times' : 'fa-bars'} text-xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden border-t py-4 backdrop-blur-md" style={{ borderColor: 'rgba(148, 137, 121, 0.3)', background: 'rgba(223, 208, 184, 0.95)' }}>
            <div className="flex flex-col space-y-1 px-4">
              {/* Show all navigation for authenticated users */}
              {user ? (
                <>
                  <Link
                    to="/home"
                    className={`px-4 py-3 rounded-lg font-medium transition-all duration-300 flex items-center ${isActive('/home') ? 'text-white shadow-lg' : 'hover:bg-gray-100'
                      }`}
                    style={{
                      backgroundColor: isActive('/home') ? '#222831' : 'transparent',
                      color: isActive('/home') ? 'white' : '#222831'
                    }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <i className="fas fa-home mr-3"></i>Home
                  </Link>
                  <Link
                    to="/rent-items"
                    className={`px-4 py-3 rounded-lg font-medium transition-all duration-300 flex items-center ${isActive('/rent-items') ? 'text-white shadow-lg' : 'hover:bg-gray-100'
                      }`}
                    style={{
                      backgroundColor: isActive('/rent-items') ? '#222831' : 'transparent',
                      color: isActive('/rent-items') ? 'white' : '#222831'
                    }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <i className="fas fa-search mr-3"></i>Browse Items
                  </Link>
                  <Link
                    to="/list-items"
                    className={`px-4 py-3 rounded-lg font-medium transition-all duration-300 flex items-center ${isActive('/list-items') ? 'text-white shadow-lg' : 'hover:bg-gray-100'
                      }`}
                    style={{
                      backgroundColor: isActive('/list-items') ? '#222831' : 'transparent',
                      color: isActive('/list-items') ? 'white' : '#222831'
                    }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <i className="fas fa-plus mr-3"></i>List Items
                  </Link>
                  <Link
                    to="/dashboard"
                    className={`px-4 py-3 rounded-lg font-medium transition-all duration-300 flex items-center ${isActive('/dashboard') ? 'text-white shadow-lg' : 'hover:bg-gray-100'
                      }`}
                    style={{
                      backgroundColor: isActive('/dashboard') ? '#222831' : 'transparent',
                      color: isActive('/dashboard') ? 'white' : '#222831'
                    }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <i className="fas fa-chart-line mr-3"></i>Dashboard
                  </Link>
                  <Link
                    to="/membership"
                    className={`px-4 py-3 rounded-lg font-medium transition-all duration-300 flex items-center ${isActive('/membership') ? 'text-white shadow-lg' : 'hover:bg-gray-100'
                      }`}
                    style={{
                      backgroundColor: isActive('/membership') ? '#393E46' : 'transparent',
                      color: isActive('/membership') ? 'white' : '#393E46'
                    }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <i className="fas fa-crown mr-3"></i>Premium
                  </Link>
                  <Link
                    to="/chat"
                    className={`px-4 py-3 rounded-lg font-medium transition-all duration-300 flex items-center relative ${isActive('/chat') ? 'text-white shadow-lg' : 'hover:bg-gray-100'
                      }`}
                    style={{
                      backgroundColor: isActive('/chat') ? '#393E46' : 'transparent',
                      color: isActive('/chat') ? 'white' : '#393E46'
                    }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <i className="fas fa-comments mr-3"></i>Chat
                    <NotificationBadge className="ml-2" />
                  </Link>

                  {/* Admin Link */}
                  {user.isAdmin && (
                    <Link
                      to="/admin"
                      className={`px-4 py-3 rounded-lg font-medium transition-all duration-300 flex items-center ${isActive('/admin') ? 'text-white shadow-lg' : 'hover:bg-gray-100'
                        }`}
                      style={{
                        backgroundColor: isActive('/admin') ? '#393E46' : 'transparent',
                        color: isActive('/admin') ? 'white' : '#393E46'
                      }}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <i className="fas fa-shield-alt mr-3"></i>Admin
                    </Link>
                  )}
                </>
              ) : null}

              {/* Mobile User Section - Only show for authenticated users */}
              {user ? (
                <div className="border-t pt-4 mt-4" style={{ borderColor: 'rgba(148, 137, 121, 0.3)' }}>
                  <div className="px-4 py-2 mb-3 flex items-center justify-between">
                    <p className="font-medium" style={{ color: '#222831' }}>
                      Hello, {user.name}
                    </p>
                    <NotificationToggle showLabel={false} className="text-xs px-2 py-1" />
                  </div>
                  <button
                    onClick={handleLogout}
                    className="px-4 py-3 rounded-lg transition-colors w-full font-medium border"
                    style={{
                      background: 'rgba(239, 68, 68, 0.1)',
                      color: '#dc2626',
                      borderColor: 'rgba(239, 68, 68, 0.3)'
                    }}
                  >
                    <i className="fas fa-sign-out-alt mr-2"></i>Logout
                  </button>
                </div>
              ) : (
                <div className="border-t pt-4 mt-4 space-y-2" style={{ borderColor: 'rgba(148, 137, 121, 0.3)' }}>
                  <Link
                    to="/login"
                    className="px-4 py-3 rounded-lg transition-colors w-full font-medium border flex items-center justify-center"
                    style={{
                      color: '#948979',
                      borderColor: 'rgba(148, 137, 121, 0.3)'
                    }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <i className="fas fa-sign-in-alt mr-2"></i>Login
                  </Link>
                  <Link
                    to="/signup"
                    className="px-4 py-3 rounded-lg transition-colors w-full font-medium flex items-center justify-center"
                    style={{
                      background: '#222831',
                      color: 'white'
                    }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <i className="fas fa-user-plus mr-2"></i>Sign Up
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;